/**
 * GroupSpaceManagementService
 * 그룹 공간 관리 비즈니스 로직
 */
import type {
  GroupSpaceType,
  ReservationStatus,
  GroupSpace,
  Reservation,
  GroupSpaceStats,
} from '../models/GroupSpaceManagement';
import { formatDateKST } from '../../../utils/dateUtils';

/**
 * GroupSpaceManagementService 클래스
 * 그룹 공간 관련 비즈니스 로직 및 유틸리티 함수
 */
export class GroupSpaceManagementService {
  /**
   * 공간 타입에 따른 아이콘 클래스 반환
   */
  getTypeIcon(type: GroupSpaceType): string {
    const iconMap: Record<GroupSpaceType, string> = {
      study_room: 'h-6 w-6 text-blue-600',
      meeting_room: 'h-6 w-6 text-green-600',
      private_room: 'h-6 w-6 text-purple-600',
    };
    return iconMap[type] || 'h-6 w-6 text-gray-600';
  }

  /**
   * 공간 타입 한글명 반환
   */
  getTypeName(type: GroupSpaceType): string {
    const nameMap: Record<GroupSpaceType, string> = {
      study_room: '스터디룸',
      meeting_room: '회의실',
      private_room: '개인실',
    };
    return nameMap[type] || '알 수 없음';
  }

  /**
   * 공간 타입 배경색 클래스 반환
   */
  getTypeBackgroundClass(type: GroupSpaceType): string {
    const bgMap: Record<GroupSpaceType, string> = {
      study_room: 'bg-blue-50',
      meeting_room: 'bg-green-50',
      private_room: 'bg-purple-50',
    };
    return bgMap[type] || 'bg-gray-50';
  }

  /**
   * 예약 상태 배지 클래스 반환
   */
  getStatusBadgeClass(status: ReservationStatus): string {
    const badgeMap: Record<ReservationStatus, string> = {
      confirmed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return badgeMap[status] || 'bg-gray-100 text-gray-800';
  }

  /**
   * 예약 상태 한글명 반환
   */
  getStatusName(status: ReservationStatus): string {
    const nameMap: Record<ReservationStatus, string> = {
      confirmed: '확정',
      pending: '대기',
      cancelled: '취소',
    };
    return nameMap[status] || '알 수 없음';
  }

  /**
   * 시간 포맷팅 (ISO → HH:MM)
   */
  formatTime(isoString: string): string {
    if (!isoString) return '--:--';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '--:--';
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '--:--';
    }
  }

  /**
   * 날짜 포맷팅 (ISO → YYYY-MM-DD)
   */
  formatDate(isoString: string): string {
    if (!isoString) return '-------';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '-------';
      return formatDateKST(date);
    } catch {
      return '-------';
    }
  }

  /**
   * 날짜+시간 포맷팅 (ISO → YYYY-MM-DD HH:MM)
   */
  formatDateTime(isoString: string): string {
    if (!isoString) return '----/--/-- --:--';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '----/--/-- --:--';
      const dateStr = formatDateKST(date);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${dateStr} ${hours}:${minutes}`;
    } catch {
      return '----/--/-- --:--';
    }
  }

  /**
   * 금액 포맷팅 (숫자 → 천 단위 구분 + '원')
   */
  formatPrice(price: number): string {
    return `${price.toLocaleString()}원`;
  }

  /**
   * 공간 활성화 상태 배지 클래스 반환
   */
  getActiveBadgeClass(isActive: boolean): string {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  }

  /**
   * 공간 활성화 상태 한글명 반환
   */
  getActiveStatusName(isActive: boolean): string {
    return isActive ? '사용 중' : '비활성';
  }

  /**
   * 그룹 공간 통계 계산
   */
  calculateStats(spaces: GroupSpace[], reservations: Reservation[]): GroupSpaceStats {
    const totalSpaces = spaces.length;
    const activeSpaces = spaces.filter((space) => space.is_active).length;
    const totalReservations = reservations.length;

    // 총 매출 계산 (확정된 예약만)
    const totalRevenue = reservations
      .filter((r) => r.status === 'confirmed')
      .reduce((sum, r) => sum + r.payment_amount, 0);

    // 점유율 계산 (현재 예약 중인 공간 / 활성 공간)
    const occupiedSpaces = spaces.filter((s) => s.current_reservation).length;
    const occupancyRate = activeSpaces > 0 ? (occupiedSpaces / activeSpaces) * 100 : 0;

    return {
      total_spaces: totalSpaces,
      active_spaces: activeSpaces,
      total_reservations: totalReservations,
      total_revenue: totalRevenue,
      occupancy_rate: Math.round(occupancyRate * 10) / 10, // 소수점 첫째자리
    };
  }

  /**
   * 사용 시간 계산 (시작 ~ 종료 시간 차이)
   */
  calculateUsageHours(startTime: string, endTime: string): number {
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMs = end.getTime() - start.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      return Math.round(diffHours * 10) / 10; // 소수점 첫째자리
    } catch {
      return 0;
    }
  }

  /**
   * 예약 가능 여부 확인
   */
  isAvailable(space: GroupSpace): boolean {
    return space.is_active && !space.current_reservation;
  }

  /**
   * 공간 타입별 필터링
   */
  filterByType(spaces: GroupSpace[], type?: GroupSpaceType): GroupSpace[] {
    if (!type) return spaces;
    return spaces.filter((space) => space.type === type);
  }

  /**
   * 활성화 상태별 필터링
   */
  filterByActiveStatus(spaces: GroupSpace[], isActive?: boolean): GroupSpace[] {
    if (isActive === undefined) return spaces;
    return spaces.filter((space) => space.is_active === isActive);
  }

  /**
   * 예약 상태별 필터링
   */
  filterReservationsByStatus(
    reservations: Reservation[],
    status?: ReservationStatus
  ): Reservation[] {
    if (!status) return reservations;
    return reservations.filter((reservation) => reservation.status === status);
  }

  /**
   * 특정 공간의 예약 필터링
   */
  filterReservationsBySpace(
    reservations: Reservation[],
    spaceId?: string
  ): Reservation[] {
    if (!spaceId) return reservations;
    return reservations.filter((reservation) => reservation.space_id === spaceId);
  }
}

/**
 * GroupSpaceManagementService 싱글톤 인스턴스
 */
export const groupSpaceManagementService = new GroupSpaceManagementService();
